import { Component, ElementRef, OnInit, ViewChild } from "@angular/core";
import { Store } from "@ngrx/store";
import { copyToClipboard, isPlatformBrowser } from '../../angular-wallet-base/utils';
import {
  WalletConnectSessionProposalPairRequestAction,
  WalletConnectSetChainRequestAction,
  WalletConnectStateInitRequestAction,
} from "../../angular-wallet-base/actions/walletConnect.actions";
import { AppState } from "../../angular-wallet-base/store/appState";
import {
  getSingularitySession,
  getWalletConnectEventRequests,
  getWalletConnectInstance,
  getWalletConnectSelectedNetwork,
  getWalletConnectSession,
  IWalletConnectSelectedNetwork
} from "../../angular-wallet-base/store/walletConnect";
import { SubscriptionListenerComponent } from "../../angular-wallet-base/SubscriptionListenerComponent";
import { QRScanner, QRScannerStatus } from "@ionic-native/qr-scanner/ngx";
import { EIP155_SIGNING_METHODS, WalletConnectService } from "../../angular-wallet-base/services/wallet-connect.service";
import { ModalController, AlertController, Platform, NavController } from "@ionic/angular";
import { ModalWalletConnectionComponent } from "../modal-wallet-connection/modal-wallet-connection.component";
import { Logger } from "../../angular-wallet-base/services/logger.service";
import { AuthService } from "../../angular-wallet-base/services/auth.service";
import { QrScannerComponent } from "../browser-scanner/browser-scanner.component";
import { EvmChain, EvmTransactionData } from "@moralisweb3/evm-utils";
import { PaginatedTransactionsComponent } from "../transaction-history-moralis/transaction-history-moralis.component";
import { take } from "rxjs/operators";
import { StorageService } from "src/app/angular-wallet-base/services/storage.service";
import { ModalDisclaimerComponent } from "./disclaimer-modal/disclaimer-modal.component";
import { WALLET_CONNECT_READ_DISCLAIMER } from "src/app/angular-wallet-base/constants";
import { isNil } from "src/app/angular-wallet-base/ts-util";
import { inspect } from "util";
import { WCModalRequestSendingTransactionComponent } from "../modal-request/wc-modal-request-sending-transaction/wc-modal-request-sending-transaction.component";
import { ScannerComponent } from "../browser-scanner/scanner/scanner.component";


@Component({
  selector: "app-wallet-connect",
  templateUrl: "./wallet-connect.component.html",
  styleUrls: ["./wallet-connect.component.scss"]
})
export class WalletConnectComponent extends SubscriptionListenerComponent implements OnInit {
  @ViewChild(PaginatedTransactionsComponent) paginatedTransactionsComponent: PaginatedTransactionsComponent;
  @ViewChild('qrTextInput', { static: false }) qrTextInput: ElementRef;

  public selectedNetwork: IWalletConnectSelectedNetwork;
  public defaultChainSymbol = 'AVAX'; // default chain is Avalanche
  public walletConnectEventRequests = [];
  public evmChain = EvmChain.AVALANCHE;
  public keychain;
  public accountInfo;
  public session;
  public scanSub: any;
  public qrText;

  public singularitySession;


  public supportedNetworks = [];
  public transactionHistory: EvmTransactionData[];

  constructor(
    private qrScanner: QRScanner,
    protected store: Store<AppState>,
    private walletConnectService: WalletConnectService,
    public modalController: ModalController,
    public alertController: AlertController,
    private authService: AuthService,
    private platform: Platform,
    private navCtrl: NavController,
    private storageService: StorageService,
  ) {
    super(store);
  }


  async ngOnInit() {
    if(!(await this.authService.hasGroup('GROUP_WALLETCONNECT'))) {
      //do not render the component
      return false;
    }

    await this.initiateWalletConnectWeb3Instance();

    await this.initiateWalletConnectSupportNetwork();

    await this.initiateSelectedSupportNetwork();

    this.store.select(getWalletConnectSelectedNetwork).subscribe(async selectedNetwork => {
      Logger.info("selectedNetwork updated through subscription", selectedNetwork);
      this.selectedNetwork = selectedNetwork;
      this.evmChain = this.selectedNetwork.chainId === 43114 ? EvmChain.AVALANCHE : EvmChain.FUJI;// todo need to change
    })

    this.store.select(getWalletConnectSession).subscribe(async session => {
      Logger.info(`new session ${inspect(session)}`);
      this.session = session;
    });
    
    this.store.select(getWalletConnectEventRequests).subscribe(async eventRequests => {
      Logger.info(`new wallet connect event request ${inspect(eventRequests)}`);
      this.walletConnectEventRequests = await eventRequests;
    });

    this.store.select(getSingularitySession).subscribe(async singulariySession => {
      Logger.info(`getSingularitySession ${inspect(singulariySession)}`);
      this.singularitySession = singulariySession;
    });


    //check if storage service has readDisclaimer
    const readDisclaimer = await this.storageService.get(WALLET_CONNECT_READ_DISCLAIMER);
    if (!readDisclaimer) {
      this.presentDisclaimer();
    }
  }


  private async initiateWalletConnectWeb3Instance() {
    const wcWeb3WalletInstance = await this.store.select(getWalletConnectInstance).pipe(take(1)).toPromise();
    if (isNil(wcWeb3WalletInstance)) {
      Logger.info(`WalletConnect Web3Wallet Instance is not initialized yet`);
      this.store.dispatch(new WalletConnectStateInitRequestAction());
    } 
  }

  private async initiateWalletConnectSupportNetwork() {
    let supportNetworkExplorers = await this.walletConnectService.getWalletConnectSupportNetworks();
    this.supportedNetworks = supportNetworkExplorers.map(supportedNetworkExplorer => {
      return {
        'id' : supportedNetworkExplorer.chain,
        'viewValue': supportedNetworkExplorer.name,
        'value': supportedNetworkExplorer.chainId,
      }
    });
  }

  private async initiateSelectedSupportNetwork() {
    this.selectedNetwork = await this.store.select(getWalletConnectSelectedNetwork).pipe(take(1)).toPromise();
    Logger.info(`initiateSelectedSupportNetwork ${inspect(this.selectedNetwork)}`);
    if (isNil(this.selectedNetwork)) {
      Logger.info(`Selected network is not initialized yet!`);
      this.store.dispatch(new WalletConnectSetChainRequestAction({baseChainSymbol: this.defaultChainSymbol}));
    } else if (!this.selectedNetwork.walletAddress) {
      Logger.info(`Wallet Address is not initialized!`);
      this.store.dispatch(new WalletConnectSetChainRequestAction({baseChainSymbol: this.selectedNetwork.baseChainSymbol}));
    }
  }

  //detect changes on wallet connect uri
  wcUriOnChange(event) {
    Logger.info("wallet connect changes", event?.target?.value);
    const wcUri = event?.target?.value;
    this.pairingWalletConnect(wcUri);
  }


  pairingWalletConnect(uri: string) {
    if (!isNil(uri)) {
      Logger.info("valid wc uri", uri);
      this.store.dispatch(new WalletConnectSessionProposalPairRequestAction({uri}));
    }
  }



  handleNetworkChange(val) {
    Logger.info("handleNetworkChange - basechainSymbol", val);
    let item = this.supportedNetworks.find(item => item.value === val);
    this.selectedNetwork.chainId = val;
    if (val != this.selectedNetwork.baseChainSymbol) {
      this.store.dispatch( new WalletConnectSetChainRequestAction({baseChainSymbol: item.id}));
    }
  }


  copyPasted($event) {
    this.qrText = $event.target.value;
  }

  async startScanning() {
    if(isPlatformBrowser(this.platform)) {
      await this.scanQROnBrowser();
    } else {
      await this.scanQROnMobileApp();
    }
  }

  private async scanQROnBrowser() {
    const modal = await this.modalController.create({
      component: ScannerComponent
    });
    modal.onDidDismiss().then(data => {
      Logger.info('scanQROnBrowser', JSON.stringify(data.data));
      this.qrText = data.data?.scannedData;
      this.pairingWalletConnect(this.qrText);
    });
    await modal.present();
  }

  // Todo: Eric need to review in the future
  private async scanQROnMobileApp() {
     // Optionally request the permission early
     this.qrScanner
     .prepare()
     .then((status: QRScannerStatus) => {
       if (status.authorized) {
         this.qrScanner.show();
         this.scanSub = document.getElementsByTagName("body")[0].style.opacity = "0";
         debugger;
         this.scanSub = this.qrScanner.scan().subscribe(
           (textFound: string) => {
             document.getElementsByTagName("body")[0].style.opacity = "1";
             this.qrScanner.hide();
             this.scanSub.unsubscribe();

             this.qrText = textFound;
           },
           err => {
             alert(JSON.stringify(err));
           }
         );
       } else if (status.denied) {
       } else {
       }
     })
     .catch((e: any) => console.log("Error is", e));
  }


  copyClipboard() {
    copyToClipboard(this.selectedNetwork.walletAddress);
  }


  // future can do multiple sessions
  async showConnection() {
    const alert = await this.modalController.create({
      component: ModalWalletConnectionComponent,
      componentProps: {
        session: this.session,
        singularitySession: this.singularitySession,
      },
      cssClass: 'modal-show-connection',
    });
    await alert.present();
  }

  async openEventRequest(eventRequest) {
    Logger.info("openEventRequest", eventRequest);
    const {topic, params} = eventRequest;
    const {request} = params;
    const web3WalletInstance = await this.store.select(getWalletConnectInstance).pipe(take(1)).toPromise();
    const requestSession = web3WalletInstance.engine.signClient.session.get(topic);

    switch (request.method) {
      // Todo for different request types
      /*
      case EIP155_SIGNING_METHODS.ETH_SIGN:
      case EIP155_SIGNING_METHODS.PERSONAL_SIGN:
        return ModalStore.open('SessionSignModal', { requestEvent, requestSession })
      */
      case EIP155_SIGNING_METHODS.ETH_SIGN:
      case EIP155_SIGNING_METHODS.PERSONAL_SIGN:
      case EIP155_SIGNING_METHODS.ETH_SIGN_TYPED_DATA:
      case EIP155_SIGNING_METHODS.ETH_SIGN_TYPED_DATA_V3:
      case EIP155_SIGNING_METHODS.ETH_SIGN_TYPED_DATA_V4:
      case EIP155_SIGNING_METHODS.ETH_SEND_TRANSACTION:
      case EIP155_SIGNING_METHODS.ETH_SIGN_TRANSACTION:
        this.openEventSendTransactionModal(eventRequest, requestSession);
        break;
      default:
        Logger.error(`Error - openEventRequest - request method is not supported ${inspect(request)}`) 
        break;
    }



    //wait 10 seconds then refresh transactions
    setTimeout(() => {
      this.paginatedTransactionsComponent?.refreshTransactions();
    }, 10000);
  }

  
  private async openEventSendTransactionModal(requestEvent, requestSession) {
    const modal = await this.modalController.create({
      component: WCModalRequestSendingTransactionComponent,
      componentProps: {
        requestEvent,
        requestSession
      },
      id: `requestEvent-${requestEvent?.id}`
    });
    await modal.present();
  }

  focusQrTextInput() {
    this.qrTextInput.nativeElement.select();
  }

  goBack() {
    this.navCtrl.back();
  }

  presentDisclaimer() {
    this.modalController.create({
      component: ModalDisclaimerComponent,
      cssClass: 'modal-disclaimer'
    }).then(modal => {
      modal.present();
    });
  }
}
